"use client";

import { useEffect, useRef, useState } from "react";
import type { Ref } from "react";
import { preload } from "react-dom";
import type HlsInstance from "hls.js";
import type { ErrorData } from "hls.js";
export type HLSLoadStrategy = "eager" | "idle" | "visible";
export type HLSTrackProps = {
  src: string;
  srcLang?: string;
  label?: string;
  kind?: "captions" | "subtitles" | "descriptions" | "chapters" | "metadata";
  default?: boolean;
};
export type HLSPlayerProps = {
  src: string;
  poster?: string;
  controls?: boolean;
  loop?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  onError?: (event: string, data: ErrorData) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onAutoplayBlocked?: (reason: string) => void;
  autoplayFallbackToMuted?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  /** Set to true for LCP videos to prioritize loading */
  priority?: boolean;
  /**
   * When to begin fetching the HLS manifest/segments (or setting video.src
   * on the native playback path). Read once on mount.
   * - "eager": on mount (default)
   * - "idle": after browser idle — for above-fold videos with a poster
   * - "visible": when the element is within 200px of the viewport
   */
  loadStrategy?: HLSLoadStrategy;
  tracks?: HLSTrackProps[];
  /**
   * Forwarded to the inner <video> element so callers can drive native APIs
   * directly (e.g. setting currentTime for scroll-driven scrubbing).
   * The internal ref used by hls.js wiring keeps working alongside this.
   */
  ref?: Ref<HTMLVideoElement | null>;
};
const handleAutoplay = async (video: HTMLVideoElement, {
  muted,
  autoplayFallbackToMuted,
  onAutoplayBlocked
}: {
  muted: boolean;
  autoplayFallbackToMuted: boolean;
  onAutoplayBlocked?: (reason: string) => void;
}) => {
  try {
    await video.play();
  } catch (error) {
    const isNotAllowedError = error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "NotSupportedError");
    if (isNotAllowedError && !muted && autoplayFallbackToMuted) {
      console.info("Autoplay blocked by browser policy, retrying with muted audio");
      video.muted = true;
      try {
        await video.play();
        console.info("Fallback to muted autoplay succeeded");
        onAutoplayBlocked?.("Autoplayed muted due to browser policy");
      } catch (mutedError) {
        console.error("Autoplay failed even when muted - potential technical issue", {
          error: mutedError,
          errorName: mutedError instanceof DOMException ? mutedError.name : "Unknown"
        });
        onAutoplayBlocked?.("Autoplay completely blocked by browser");
      }
    } else if (isNotAllowedError) {
      console.info("Autoplay blocked by browser policy", {
        errorName: error instanceof DOMException ? error.name : "Unknown",
        muted
      });
      onAutoplayBlocked?.("Autoplay blocked by browser policy");
    } else {
      console.error("Unexpected autoplay error - potential technical issue", {
        error,
        errorName: error instanceof DOMException ? error.name : "Unknown",
        muted
      });
      onAutoplayBlocked?.("Autoplay failed");
    }
  }
};
export const HLSPlayer = ({
  src,
  poster,
  autoPlay = false,
  controls = true,
  muted = false,
  loop = false,
  width = "100%",
  height = "auto",
  className = "",
  onError,
  onPlay,
  onPause,
  onEnded,
  onAutoplayBlocked,
  autoplayFallbackToMuted = true,
  priority = false,
  loadStrategy = "eager",
  tracks,
  ref: externalRef
}: HLSPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<HlsInstance | null>(null);
  const attachVideoRef = (el: HTMLVideoElement | null) => {
    videoRef.current = el;
    if (typeof externalRef === "function") {
      externalRef(el);
    } else if (externalRef) {
      externalRef.current = el;
    }
  };
  // Gates segment fetching — flips false→true once per mount, so the
  // playback effect below is a no-op until the strategy allows loading
  const [loadAllowed, setLoadAllowed] = useState(loadStrategy === "eager");
  useEffect(() => {
    if (loadAllowed) {
      return;
    }
    if (loadStrategy === "idle") {
      if (typeof window.requestIdleCallback === "function") {
        const id = window.requestIdleCallback(() => setLoadAllowed(true), {
          timeout: 2500
        });
        return () => window.cancelIdleCallback(id);
      }
      // Safari has no requestIdleCallback
      const timeoutId = window.setTimeout(() => setLoadAllowed(true), 2000);
      return () => clearTimeout(timeoutId);
    }

    // "visible"
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === "undefined") {
      setLoadAllowed(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setLoadAllowed(true);
      }
    }, {
      rootMargin: "200px 0px"
    });
    observer.observe(video);
    return () => observer.disconnect();
  }, [loadStrategy, loadAllowed]);

  // Preload poster image for LCP optimization
  if (priority && poster) {
    preload(poster, {
      as: "image",
      fetchPriority: "high"
    });
  }
  useEffect(() => {
    const video_0 = videoRef.current;
    if (!video_0) return;
    if (!loadAllowed) return;

    // WCAG 2.3.3 / 2.2.2 — honor prefers-reduced-motion by skipping autoplay
    const prefersReducedMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
    const shouldAutoplay = autoPlay && !prefersReducedMotion;
    let isCancelled = false;
    const canPlayNatively = video_0.canPlayType("application/vnd.apple.mpegurl") !== "";
    const setupNativePlayback = () => {
      video_0.src = src;
      if (shouldAutoplay) {
        handleAutoplay(video_0, {
          muted,
          autoplayFallbackToMuted,
          onAutoplayBlocked
        });
      }
    };

    // hls.js is ~80KB gzipped — import it on demand so pages with video
    // blocks don't carry it in their initial bundle
    const setupPlayback = async () => {
      const {
        default: Hls
      } = await import("hls.js");
      if (isCancelled) {
        return;
      }
      if (Hls.isSupported()) {
        // VOD tuning — lowLatencyMode is an LL-HLS live-streaming feature
        // and skews ABR; the buffer caps keep a hero loop from pulling tens
        // of MB of segments it may never play
        const hls = new Hls({
          enableWorker: true,
          capLevelToPlayerSize: true,
          startLevel: 0,
          maxBufferLength: 12,
          maxMaxBufferLength: 30,
          maxBufferSize: 20 * 1000 * 1000,
          backBufferLength: 30
        });
        hls.loadSource(src);
        hls.attachMedia(video_0);
        hls.on(Hls.Events.MANIFEST_PARSED, async () => {
          if (shouldAutoplay) {
            await handleAutoplay(video_0, {
              muted,
              autoplayFallbackToMuted,
              onAutoplayBlocked
            });
          }
        });
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                console.error(new Error(`Fatal HLS error: ${data.type}`));
                hls.destroy();
                break;
            }
          }
          onError?.(event, data);
        });
        hlsRef.current = hls;
      } else if (canPlayNatively) {
        setupNativePlayback();
      } else {
        // Browser doesn't support HLS at all (very rare - only very old browsers)
        // Not an actionable error - this is a user's browser choice, not something we can fix
        // User will see poster image or browser's default video UI
        console.warn("HLS not supported in this browser", {
          userAgent: navigator.userAgent
        });
      }
    };

    // Hls.isSupported() requires MediaSource (or ManagedMediaSource on
    // iOS 17+), so without either the hls.js import could never be used —
    // browsers like iOS Safari go straight to native playback and skip
    // downloading the chunk entirely
    const hasMediaSource = typeof MediaSource !== "undefined" || typeof (window as {
      ManagedMediaSource?: unknown;
    }).ManagedMediaSource !== "undefined";
    if (hasMediaSource) {
      setupPlayback().catch(error => {
        console.error("Failed to load hls.js", {
          error
        });
        // Chunk load failed — native playback still works where supported
        if (!isCancelled && canPlayNatively) {
          setupNativePlayback();
        }
      });
    } else if (canPlayNatively) {
      setupNativePlayback();
    } else {
      console.warn("HLS not supported in this browser", {
        userAgent: navigator.userAgent
      });
    }
    return () => {
      isCancelled = true;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, autoPlay, muted, autoplayFallbackToMuted, onError, onAutoplayBlocked, loadAllowed]);
  const hasTracks = !!tracks && tracks.length > 0;
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption -- caller-supplied <track> elements rendered when provided; HLS streams may also carry captions in the manifest
    <video ref={attachVideoRef} poster={poster} controls={controls} muted={muted} loop={loop} playsInline preload={priority ? "auto" : "metadata"} width={width} height={height} className={className} onPlay={onPlay} onPause={onPause} onEnded={onEnded}>
      {hasTracks && tracks!.map((track, i) => <track key={`${track.src}-${i}`} src={track.src} srcLang={track.srcLang} label={track.label} kind={track.kind ?? "captions"} default={track.default} />)}
    </video>
  );
};